import React from "react"
import ReactApexChart from "react-apexcharts"

import KpiCard from "../parts/KpiCard"
import MetricHint from "../parts/MetricHint"
import {
	Card, EmptyNote, baseChartOptions, CHART_COLORS,
	convert, formatAmount, formatNumber, formatPercent,
} from "../parts/common"

/**
 * Обзор — единственная вкладка, которая грузится сразу при открытии страницы.
 * Четыре этажа сверху вниз: масштаб → оборот клиентов → наши деньги → удержание.
 */
function Overview({ data, currency, rates }) {
	const months = data.months || []
	const kpi = data.kpi || {}
	const state = data.state || {}

	if (months.length === 0) {
		return (
			<Card>
				<EmptyNote>
					Свёртка пуста. Запустите историческую перезаливку: <code>php artisan analytics:backfill</code>
				</EmptyNote>
			</Card>
		)
	}

	// Последний ПОЛНЫЙ месяц: по нему считаются потоковые метрики, по нему же
	// пересчитываем в доллары (у каждого месяца свой курс).
	const currentMonth = new Date().toISOString().slice(0, 7)
	const full = months.filter((row) => row.month !== currentMonth)
	const lastMonth = full.length > 0 ? full[full.length - 1].month : months[months.length - 1].month

	const amount = (value, month = lastMonth) => formatAmount(convert(value, month, currency, rates), currency)
	const series = (key) => full.map((row) => Number(convert(row[key], row.month, currency, rates).toFixed(2)))
	const categories = full.map((row) => row.label)

	const gtvChart = {
		options: {
			...baseChartOptions,
			chart: { ...baseChartOptions.chart, type: "line", height: 340, stacked: false },
			colors: [CHART_COLORS[0], CHART_COLORS[1], CHART_COLORS[3]],
			stroke: { width: [0, 3, 3], curve: "smooth" },
			xaxis: { ...baseChartOptions.xaxis, categories },
			yaxis: [
				{
					title: { text: "Оборот клиентов", style: { color: "#8791a1", fontSize: "11px" } },
					labels: {
						style: { colors: "#8791a1", fontSize: "11px" },
						formatter: (value) => formatAmount(value, currency),
					},
				},
				{
					opposite: true,
					title: { text: "Точки и бренды", style: { color: "#8791a1", fontSize: "11px" } },
					labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => Math.round(value) },
				},
				{ show: false },
			],
			tooltip: {
				theme: "light",
				y: {
					formatter: (value, { seriesIndex }) =>
						seriesIndex === 0 ? formatAmount(value, currency) : formatNumber(value),
				},
			},
		},
		series: [
			{ name: "Оборот клиентов", type: "column", data: series("gtv") },
			{ name: "Точек работает", type: "line", data: full.map((row) => row.working_points) },
			{ name: "Брендов работает", type: "line", data: full.map((row) => row.working_brands) },
		],
	}

	const moneyChart = {
		options: {
			...baseChartOptions,
			chart: { ...baseChartOptions.chart, type: "line", height: 300 },
			colors: [CHART_COLORS[1], CHART_COLORS[2], CHART_COLORS[4]],
			stroke: { width: 3, curve: "smooth" },
			xaxis: { ...baseChartOptions.xaxis, categories },
			yaxis: {
				labels: {
					style: { colors: "#8791a1", fontSize: "11px" },
					formatter: (value) => formatAmount(value, currency),
				},
			},
			tooltip: { theme: "light", y: { formatter: (value) => formatAmount(value, currency) } },
		},
		series: [
			{ name: "MRR", data: series("mrr") },
			{ name: "Начислено", data: series("billed") },
			{ name: "Собрано", data: series("collected") },
		],
	}

	return (
		<>
			<div className="row g-2 mb-2">
				<div className="col-md-3">
					<KpiCard title="Брендов" hintKey="metric_brand_hint" value={formatNumber(kpi.brands?.value)}
						sublabel="клиентов с договором" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Точек подключено" hintKey="metric_active_points_hint"
						value={formatNumber(kpi.points?.value)}
						sublabel={`отключено за неоплату: ${formatNumber(state.suspended_points)}`} />
				</div>
				<div className="col-md-3">
					<KpiCard title="Точек работает" hintKey="metric_mal_hint"
						value={formatNumber(kpi.working_points?.value)}
						sublabel={kpi.period_label}
						growth={kpi.working_points?.change_percent}
						yoy={kpi.working_points?.yoy_percent}
						sparkline={kpi.working_points?.sparkline} accent="#1f9d57" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Точек на бренд" hintKey="metric_points_per_brand_hint"
						value={Number(kpi.points_per_brand?.value || 0).toFixed(2)}
						sublabel="глубина проникновения" />
				</div>
			</div>

			<div className="row g-2 mb-2">
				<div className="col-md-3">
					<KpiCard title="Оборот клиентов" hintKey="metric_gtv_hint"
						value={amount(kpi.gtv?.value)} sublabel={kpi.period_label}
						growth={kpi.gtv?.change_percent} yoy={kpi.gtv?.yoy_percent}
						sparkline={kpi.gtv?.sparkline} />
				</div>
				<div className="col-md-3">
					<KpiCard title="Чеков" hintKey="metric_cheques_hint"
						value={formatNumber(kpi.cheques?.value)} sublabel={kpi.period_label}
						growth={kpi.cheques?.change_percent} yoy={kpi.cheques?.yoy_percent}
						sparkline={kpi.cheques?.sparkline} accent="#9b5de5" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Средний чек" hintKey="metric_aov_hint"
						value={amount(kpi.avg_cheque?.value)} sublabel={kpi.period_label}
						growth={kpi.avg_cheque?.change_percent} yoy={kpi.avg_cheque?.yoy_percent}
						sparkline={kpi.avg_cheque?.sparkline} accent="#f0a04c" />
				</div>
				<div className="col-md-3">
					<KpiCard title="MRR / ARR" hintKey="metric_mrr_hint"
						value={amount(kpi.mrr?.value)}
						sublabel={`ARR ${amount(kpi.arr?.value)} · ARPA ${amount(kpi.arpa?.value)}`}
						sparkline={kpi.mrr?.sparkline} accent="#1f9d57" />
				</div>
			</div>

			<div className="row g-2 mb-3">
				<div className="col-md-3">
					<KpiCard title="Take rate" hintKey="metric_take_rate_hint"
						value={formatPercent(kpi.take_rate?.value, 3)}
						sublabel="доля оборота клиента, которую берём мы"
						growth={kpi.take_rate?.change_percent} />
				</div>
				<div className="col-md-3">
					<KpiCard title="Собираемость" hintKey="metric_collection_rate_hint"
						value={formatPercent(kpi.collection_rate?.value)}
						sublabel={kpi.period_label} growth={kpi.collection_rate?.change_percent} />
				</div>
				<div className="col-md-3">
					<KpiCard title="Отток в месяц" hintKey="metric_churn_hint"
						value={formatPercent(kpi.churn_rate?.value, 2)}
						sublabel={kpi.churn_rate?.note} accent="#d23434" />
				</div>
				<div className="col-md-3">
					<KpiCard title="LTV" hintKey="metric_ltv_hint"
						value={kpi.ltv?.value ? amount(kpi.ltv.value) : "—"}
						sublabel={kpi.ltv?.note || "доход с клиента за всю жизнь"} />
				</div>
			</div>

			<div className="row g-2 mb-2">
				<div className="col-12">
					<Card title="Оборот клиентов и сеть по месяцам" hint={<MetricHint hintKey="metric_gtv_hint" />}>
						<ReactApexChart options={gtvChart.options} series={gtvChart.series} type="line" height={340} />
					</Card>
				</div>
			</div>

			<div className="row g-2 mb-2">
				<div className="col-12">
					<Card title="Наши деньги: MRR, начислено, собрано" hint={<MetricHint hintKey="metric_mrr_hint" />}>
						<ReactApexChart options={moneyChart.options} series={moneyChart.series} type="line" height={300} />
					</Card>
				</div>
			</div>

			<Card title="По месяцам">
				<div className="table-responsive">
					<table className="table table-sm table-hover table-bordered mb-0" style={{ fontSize: "12px" }}>
						<thead>
							<tr>
								<th className="text-center">Месяц</th>
								<th className="text-end">Оборот</th>
								<th className="text-end">Чеков</th>
								<th className="text-end">Средний чек</th>
								<th className="text-end">Точек</th>
								<th className="text-end">Брендов</th>
								<th className="text-end">Новых точек</th>
								<th className="text-end">Новых брендов</th>
								<th className="text-end">Расширение сетей</th>
								<th className="text-end">MRR</th>
								<th className="text-end">Take rate</th>
							</tr>
						</thead>
						<tbody>
							{months.map((row) => (
								<tr key={row.month}>
									<td className="text-center">{row.label}</td>
									<td className="text-end">{amount(row.gtv, row.month)}</td>
									<td className="text-end">{formatNumber(row.cheques)}</td>
									<td className="text-end">{amount(row.avg_cheque, row.month)}</td>
									<td className="text-end">{formatNumber(row.working_points)}</td>
									<td className="text-end">{formatNumber(row.working_brands)}</td>
									<td className="text-end">{formatNumber(row.new_points)}</td>
									<td className="text-end">{formatNumber(row.new_brands)}</td>
									<td className="text-end">{formatNumber(row.expansion_points)}</td>
									<td className="text-end">{amount(row.mrr, row.month)}</td>
									<td className="text-end">{formatPercent(row.take_rate, 3)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Card>
		</>
	)
}

export default Overview
